import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import dayjs from 'services/dayjs';
import useNumberParams from 'hooks/useNumberParams';
import useProfileConfig from 'hooks/useProfileConfig';
import {
  FilterFragment as Filter,
  FilterInput,
  FilterType,
  RefetchType,
  ReportScreenInput,
  ReportsDashboardInput,
} from 'generated/graphql';
import {
  hasReportsDashboardUrlParams,
  parseReportsDashboardStateFromSearch,
  ReportsDashboardContextProps,
  ReportsDashboardState,
} from './reports.utils';

const Context = createContext<ReportsDashboardContextProps | undefined>(undefined);

const getDefaultFilters = (filters: Filter[] | null = null): FilterInput[] => {
  if (filters) {
    return filters.map((filter: Filter) => ({
      type: filter.type,
      selectedIDs: [],
    }));
  }
  return Object.values(FilterType).map((type) => ({
    type,
    selectedIDs: [],
  }));
};

const defaultDashboardState = (): ReportsDashboardState => ({
  startDate: dayjs().subtract(7, 'days'),
  endDate: dayjs(),
  packingDate: dayjs(),
  searchValue: '',
  filteredSearchValue: '',
  filters: getDefaultFilters(null),
  shipmentReference: '',
  printerFriendly: false,
  showLinkingError: false,
  openTechnoTermsDialog: false,
});

const buildInitialDashboardState = (): ReportsDashboardState => {
  const base = defaultDashboardState();
  if (typeof window === 'undefined') {
    return base;
  }
  return { ...base, ...parseReportsDashboardStateFromSearch(window.location.search) };
};

export const ReportsDashboardProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [getNumberParam] = useNumberParams();
  const { profileID } = useProfileConfig();
  const productID = getNumberParam('productID') ?? 0;

  const [reportsDashboardState, setReportsDashboardState] = useState<ReportsDashboardState>(buildInitialDashboardState);
  const { startDate, endDate, packingDate, filteredSearchValue, filters, shipmentReference, printerFriendly } =
    reportsDashboardState;

  useEffect(() => {
    if (!hasReportsDashboardUrlParams(location.search)) {
      return;
    }

    const fromUrl = parseReportsDashboardStateFromSearch(location.search);
    setReportsDashboardState((prev) => ({ ...prev, ...fromUrl }));
  }, [location.search]);

  // Shared input for dashboard providers (Pricing, Inventory, Chart, Table).
  const reportsDashboardQueryInput: ReportScreenInput = useMemo(
    () => ({
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      packingDate: packingDate.toISOString(),
      search: filteredSearchValue ? filteredSearchValue.toLowerCase() : '',
      productID,
      filters,
      shipmentReference,
      pagination: {
        horizontal: {
          ids: [],
          pageNumber: 1,
          pageSize: 10,
          type: RefetchType.ProductType,
        },
        vertical: {
          ids: [],
          pageNumber: 1,
          pageSize: 25,
          type: RefetchType.ProductType,
        },
      },
    }),
    [startDate, endDate, packingDate, filteredSearchValue, productID, filters, shipmentReference],
  );

  // Shared input for dashboard providers (Pricing, Inventory, Chart, Table).
  const reportsDashboardNewQueryInput: ReportsDashboardInput = useMemo(
    () => ({
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      search: filteredSearchValue ? filteredSearchValue.toLowerCase() : '',
      productID,
      filters,
      shipmentReference,
      printerFriendly,
      profileID: profileID ?? '0',
    }),
    [startDate, endDate, filteredSearchValue, productID, filters, shipmentReference, printerFriendly, profileID],
  );

  const value = useMemo(
    () => ({
      reportsDashboardState,
      setReportsDashboardState,
      reportsDashboardQueryInput,
      reportsDashboardNewQueryInput,
    }),
    [reportsDashboardState, reportsDashboardQueryInput, reportsDashboardNewQueryInput],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useReportsDashboard = () => {
  const result = useContext(Context);
  if (!result) throw new Error('useReportsDashboard must be used within a ReportsDashboardNewProvider');
  return result;
};
